import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { deleteWorkstream } from './client';
import { keys } from './keys';

/**
 * Hard-delete a whole workstream (its agent state + chat + every S3 artifact,
 * then the BFF row) — the mutation behind the rail's Delete confirm dialog.
 * `mutate(workstreamId)` calls `DELETE /workstreams/{id}`; on settle it
 * invalidates `keys.workstream.listMine()` so the refetched rail drops the
 * deleted card (the list is read in one place, so per frontend-architecture R7
 * this is the pending-UI + invalidate path, not an optimistic cache edit).
 *
 * Consumers drive the double-submit guard off `isPending` (disable the Delete
 * control) and surface `isError` in the dialog. Errors also route to the shared
 * `MutationCache.onError` sink (reportError) via the QueryClient config.
 */
export function useDeleteWorkstream(workstreamId: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.workstream.deleteWorkstream(workstreamId),
    mutationFn: () => deleteWorkstream(workstreamId, { getAccessToken }),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.workstream.listMine(),
      });
    },
  });
}
